import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createTool, type CreateToolPayload } from '../api/skillScoutApi';

function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function toYaml(f: CreateToolPayload): string {
  const kw = f.keywords.length ? `[${f.keywords.map((k) => k.trim()).filter(Boolean).join(', ')}]` : '[]';
  return [
    `- slug: ${f.slug || '<slug>'}`,
    `  name: ${f.name || '<name>'}`,
    `  type: ${f.type}`,
    `  description: "${f.description.replace(/"/g, '\\"')}"`,
    `  keywords: ${kw}`,
    `  install_cmd: "${f.install_cmd.replace(/"/g, '\\"')}"`,
    `  requires_api_key: ${f.requires_api_key}`,
    `  weekly_downloads: ${f.weekly_downloads}`,
    `  verified: true`,
  ].join('\n');
}

const EMPTY: CreateToolPayload = {
  slug: '',
  name: '',
  type: 'skill',
  description: '',
  keywords: [],
  install_cmd: '',
  requires_api_key: false,
  weekly_downloads: 0,
};

export function AddTool() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateToolPayload>(EMPTY);
  const [kwRaw, setKwRaw] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function set<K extends keyof CreateToolPayload>(key: K, value: CreateToolPayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleName(val: string) {
    set('name', val);
    if (!slugManual) set('slug', slugify(val));
  }

  function handleSlug(val: string) {
    setSlugManual(true);
    set('slug', val);
  }

  function handleKeywords(val: string) {
    setKwRaw(val);
    set('keywords', val.split(',').map((k) => k.trim()).filter(Boolean));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await createTool(form);
      setSaved(true);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="text-4xl">✓</div>
          <h2 className="text-xl font-semibold">Tool added to registry</h2>
          <p className="text-gray-400 text-sm">{form.name} is now available as a curated tool.</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setForm(EMPTY); setKwRaw(''); setSlugManual(false); setSaved(false); }}
              className="rounded-lg bg-surface px-4 py-2 text-sm font-medium hover:bg-card transition-colors"
            >
              Add another
            </button>
            <Link
              to={`/registry/${encodeURIComponent(form.slug)}`}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              View in registry →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Add Tool</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manually add a curated tool to the registry. It appears immediately.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left — form fields */}
          <div className="space-y-4">
            <Field label="Name" required>
              <input
                className={input}
                value={form.name}
                onChange={(e) => handleName(e.target.value)}
                placeholder="marketingskills"
                required
              />
            </Field>

            <Field label="Slug" hint="Auto-generated; edit to override" required>
              <input
                className={input}
                value={form.slug}
                onChange={(e) => handleSlug(e.target.value)}
                placeholder="coreyhaines31--marketingskills"
                required
              />
            </Field>

            <Field label="Type" required>
              <select
                className={input}
                value={form.type}
                onChange={(e) => set('type', e.target.value as CreateToolPayload['type'])}
              >
                <option value="skill">skill</option>
                <option value="mcp">mcp</option>
                <option value="plugin">plugin</option>
              </select>
            </Field>

            <Field label="Description">
              <textarea
                className={`${input} h-20 resize-none`}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="What this tool does…"
              />
            </Field>

            <Field label="Keywords" hint="Comma-separated">
              <input
                className={input}
                value={kwRaw}
                onChange={(e) => handleKeywords(e.target.value)}
                placeholder="marketing, seo, cro, copywriting"
              />
            </Field>

            <Field label="Install command">
              <input
                className={input}
                value={form.install_cmd}
                onChange={(e) => set('install_cmd', e.target.value)}
                placeholder="claude mcp add owner/repo  or  # See README: …"
              />
            </Field>

            <div className="flex gap-6">
              <Field label="Weekly downloads">
                <input
                  className={`${input} w-32`}
                  type="number"
                  min={0}
                  value={form.weekly_downloads}
                  onChange={(e) => set('weekly_downloads', Number(e.target.value))}
                />
              </Field>
              <Field label=" ">
                <label className="flex items-center gap-2 cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    checked={form.requires_api_key}
                    onChange={(e) => set('requires_api_key', e.target.checked)}
                    className="accent-secondary w-4 h-4"
                  />
                  <span className="text-sm text-gray-300">Requires API key</span>
                </label>
              </Field>
            </div>

            {error && (
              <div className="rounded-lg bg-red-900/40 border border-red-700 p-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving || !form.name || !form.slug}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Adding…' : 'Add to registry'}
            </button>
          </div>

          {/* Right — YAML preview */}
          <div>
            <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              YAML preview
            </div>
            <pre className="rounded-xl bg-surface p-4 text-xs text-gray-300 font-mono leading-relaxed whitespace-pre overflow-x-auto h-full min-h-[320px]">
              {toYaml(form)}
            </pre>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
        {hint && <span className="text-gray-500 font-normal ml-1 text-xs">— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

const input =
  'w-full rounded-lg bg-surface border border-gray-700 px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors';
